import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn } from './auth';
import { HomePage } from './features/home/HomePage';
import { RoomPage } from './features/room/RoomPage';
import { SessionEndedPage } from './features/room/SessionEndedPage';
import { RecordingRequestPage } from './features/room/RecordingRequestPage';
import { SessionUnavailablePage } from './features/room/SessionUnavailablePage';
import { JoinPage } from './features/room/JoinPage';
import { TestPage } from './features/testing/TestPage';
import { RecordingCompressionTestPage } from './features/testing/RecordingCompressionTestPage';
import { SidebarPanelTestPage } from './features/testing/SidebarPanelTestPage';
import { CreateEventPage } from './features/create/CreateEventPage';
import { SignInPage } from './features/auth/SignInPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { CompleteProfilePage } from './features/auth/CompleteProfilePage';
import { InvitationPage } from './features/invite/InvitationPage';
import { ModelInvitationPage } from './features/invite/ModelInvitationPage';
import { ReservationConfirmedPage } from './features/invite/ReservationConfirmedPage';
import { ModelSignupPage } from './features/model-signup/ModelSignupPage';
import { ApplicationSubmittedPage } from './features/model-signup/ApplicationSubmittedPage';
import { EditProfilePage } from './features/profile/EditProfilePage';
import { ProfilePage } from './features/profile/ProfilePage';
import { ForArtistsPage } from './features/marketing/ForArtistsPage';
import { ForModelsPage } from './features/marketing/ForModelsPage';
import { HostDashboardPage } from './features/hosting/HostDashboardPage';
import { JobBoardPage } from './features/jobs/JobBoardPage';
import { ModelsDirectoryPage } from './features/models-directory/ModelsDirectoryPage';
import { EventsPage as PublicEventsPage } from './features/events/EventsPage';
import { AdminRoute } from './features/admin/AdminRoute';
import { AdminLayout } from './features/admin/AdminLayout';
import { ApplicationsPage } from './features/admin/applications/ApplicationsPage';
import { ProfilesPage } from './features/admin/profiles/ProfilesPage';
import { EventsPage } from './features/admin/events/EventsPage';
import { RefundRequestsPage } from './features/admin/refunds/RefundRequestsPage';
import { CRMPage } from './features/admin/crm/CRMPage';
import { LeadGenerationPage } from './features/admin/lead-generation/LeadGenerationPage';
import { ReferencePacksPage } from './features/admin/reference-packs/ReferencePacksPage';
import { TestStatusPage } from './features/admin/test-status/TestStatusPage';
import { DesignReviewPage } from './features/admin/design-review/DesignReviewPage';
import { PlansPage } from './features/admin/plans/PlansPage';
import { AdminModerationPage } from './features/admin/moderation/AdminModerationPage';
import { AccountDeletionRequestsPage } from './features/admin/account-deletion/AccountDeletionRequestsPage';
import { UserSupportPage } from './features/admin/user-support/UserSupportPage';
import { RecordingReviewPage } from './features/model-dashboard/RecordingReviewPage';
import { ReferencePackPage } from './features/reference-packs/ReferencePackEditorPage';
import { PurchasedReferencePackPage } from './features/packs/PurchasedReferencePackPage';
import { PublicUploadPage } from './features/upload/PublicUploadPage';
import { EmailPreferencesPage } from './features/email-preferences/EmailPreferencesPage';
import { TypographyAuditPage } from './features/typography/TypographyAuditPage';
import { TypographyProvider } from './features/typography/TypographyProvider';
import { TypographyTweakPanel } from './features/typography/TypographyTweakPanel';
import { FinancialsPage } from './features/financials/FinancialsPage';
import { CopyProvider } from './shared/copy/CopyProvider';
import { CurrentProfileProvider } from './shared/CurrentProfileProvider';
import { ReferencePackLicensesPage } from './features/legal/ReferencePackLicensesPage';
import { TermsPage } from './features/legal/TermsPage';
import { PrivacyPage } from './features/legal/PrivacyPage';
import { ReportConcernPage } from './features/support/ReportConcernPage';

// Redirect old dashboard URLs to the event page
function EventDashboardRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    navigate(code ? `/s/${code}` : '/dashboard', { replace: true });
  }, [code, navigate]);

  return null;
}

function SessionRouteRedirect({ action }: { action: string }) {
  const { code, roomCode } = useParams<{ code?: string; roomCode?: string }>();
  const location = useLocation();
  const sessionCode = code || roomCode;

  if (!sessionCode) {
    return <Navigate to="/" replace />;
  }

  return <Navigate to={`/s/${sessionCode}/${action}${location.search}`} replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function App() {
  return (
    <CopyProvider>
      <CurrentProfileProvider>
        <TypographyProvider>
          <div className="min-h-screen">
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<PublicEventsPage />} />
          <Route path="/jobs" element={<JobBoardPage />} />
          <Route path="/artists" element={<ForArtistsPage />} />
          <Route path="/for-artists" element={<ForArtistsPage />} />
          <Route path="/models" element={<ModelsDirectoryPage />} />
          <Route path="/models/apply" element={<ForModelsPage />} />
          <Route path="/models/apply/application-form" element={<ProtectedRoute><ModelSignupPage /></ProtectedRoute>} />
          <Route path="/models/apply/submitted" element={<ProtectedRoute><ApplicationSubmittedPage /></ProtectedRoute>} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up" element={<Navigate to="/sign-in" replace />} />
          <Route path="/sign-up/profile" element={<CompleteProfilePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/create" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><EditProfilePage /></ProtectedRoute>} />
          <Route path="/financials" element={<ProtectedRoute><FinancialsPage /></ProtectedRoute>} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/report-a-concern" element={<ReportConcernPage />} />
          <Route path="/email-preferences/:token" element={<EmailPreferencesPage />} />
          <Route path="/reference-pack-licenses" element={<ReferencePackLicensesPage />} />
          <Route path="/recordings/review" element={<ProtectedRoute><RecordingReviewPage /></ProtectedRoute>} />
          <Route path="/dashboard/packs/:productId" element={<PurchasedReferencePackPage />} />
          <Route path="/reference-packs/:roomCode" element={<SessionRouteRedirect action="packs" />} />
          <Route path="/dashboard" element={<ProtectedRoute><HostDashboardPage /></ProtectedRoute>} />
          <Route path="/hosting" element={<Navigate to="/dashboard" replace />} />
          <Route path="/profile/:id" element={<ProfilePage />} />
          <Route path="/u/:handle" element={<ProfilePage />} />
          <Route path="/event/:code" element={<ProtectedRoute><EventDashboardRedirect /></ProtectedRoute>} />
          <Route path="/dashboard/:code" element={<ProtectedRoute><EventDashboardRedirect /></ProtectedRoute>} />
          <Route path="/s/:code" element={<InvitationPage />} />
          <Route path="/s/:code/join" element={<ProtectedRoute><JoinPage /></ProtectedRoute>} />
          <Route path="/s/:code/room" element={<ProtectedRoute><RoomPage /></ProtectedRoute>} />
          <Route path="/s/:code/waiting" element={<ProtectedRoute><SessionRouteRedirect action="join" /></ProtectedRoute>} />
          <Route path="/s/:code/summary" element={<SessionEndedPage />} />
          <Route path="/s/:code/recording" element={<RecordingRequestPage />} />
          <Route path="/s/:code/unavailable" element={<SessionUnavailablePage />} />
          <Route path="/s/:code/confirmed" element={<ProtectedRoute><ReservationConfirmedPage /></ProtectedRoute>} />
          <Route path="/s/:code/upload" element={<PublicUploadPage />} />
          <Route path="/s/:code/reference-packs" element={<SessionRouteRedirect action="packs" />} />
          <Route path="/s/:code/packs" element={<ProtectedRoute><ReferencePackPage /></ProtectedRoute>} />
          <Route path="/s/:code/model-invite" element={<ModelInvitationPage />} />
          <Route path="/room/:code" element={<SessionRouteRedirect action="room" />} />
          <Route path="/join/:code" element={<SessionRouteRedirect action="join" />} />
          <Route path="/waiting/:code" element={<SessionRouteRedirect action="join" />} />
          <Route path="/session-ended/:code" element={<SessionRouteRedirect action="summary" />} />
          <Route path="/recordings/request/:code" element={<SessionRouteRedirect action="recording" />} />
          <Route path="/session-unavailable/:code" element={<SessionRouteRedirect action="unavailable" />} />
          <Route path="/upload/:roomCode" element={<SessionRouteRedirect action="upload" />} />
          <Route path="/model-invite/:code" element={<SessionRouteRedirect action="model-invite" />} />
          <Route path="/typography" element={<TypographyAuditPage />} />
          <Route path="/test" element={<TestPage />} />
          <Route path="/test/sidebar-panel" element={<SidebarPanelTestPage />} />
          <Route path="/test/recording-compression" element={<RecordingCompressionTestPage />} />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/admin/lead-generation" replace />} />
            <Route path="lead-generation" element={<LeadGenerationPage />} />
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="user-support" element={<UserSupportPage />} />
            <Route path="profiles" element={<ProfilesPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="refunds" element={<RefundRequestsPage />} />
            <Route path="reference-packs" element={<ReferencePacksPage />} />
            <Route path="moderation" element={<AdminModerationPage />} />
            <Route path="account-deletion" element={<AccountDeletionRequestsPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="design-review" element={<DesignReviewPage />} />
            <Route path="test-status" element={<TestStatusPage />} />
            <Route path="crm" element={<CRMPage />} />
          </Route>
          </Routes>
            <TypographyTweakPanel />
          </div>
        </TypographyProvider>
      </CurrentProfileProvider>
    </CopyProvider>
  );
}

export default App;
